import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const SHIRT_REQUIRED_FIELDS = [
  { key: 'neck', label: 'Neck/Collar', min: 1 },
  { key: 'chest', label: 'Chest', min: 1 },
  { key: 'waist', label: 'Waist', min: 1 },
  { key: 'belly', label: 'Belly', min: 1 },
  { key: 'seat', label: 'Seat', min: 1 },
  { key: 'shoulder', label: 'Shoulder', min: 1 },
  { key: 'backShoulderSlope', label: 'Shoulder Slope', min: 0 },
  { key: 'backToFtHps', label: 'Bk to FT HPS', min: 0, defaultValue: 0.5 },
  { key: 'distanceToWaist', label: 'Distance to Waist', min: 0, defaultValue: 17.5 },
  { key: 'shirtLengthBack', label: 'Shirt Length Back', min: 1, defaultValue: 31 },
  { key: 'shirtLengthFront', label: 'Shirt Length Front', min: 1 },
  { key: 'armholeScyeDepth', label: 'Armscye Depth', min: 1 },
  { key: 'acrossBack', label: 'X-Back', min: 0.5 },
  { key: 'acrossFront', label: 'X-Front', min: 0.5 },
  { key: 'bicepWidth', label: 'Biceps', min: 1 },
  { key: 'elbowWidth', label: 'Elbow', min: 1 },
  { key: 'maxCuffWidth', label: 'Sleeve Open', min: 1 },
  { key: 'sleeveLength', label: 'Sleeve Length', min: 1 },
];

const TROUSER_REQUIRED_FIELDS = [
  { key: 'waist', label: 'Waist', min: 1 },
  { key: 'seat', label: 'Seat', min: 1 },
  { key: 'rise', label: 'Rise', min: 1 },
  { key: 'outseam', label: 'Outseam', min: 1 },
  { key: 'thigh', label: 'Thigh', min: 1 },
  { key: 'knee', label: 'Knee', min: 1 },
  { key: 'hem', label: 'Bottom/Hem', min: 1 },
];

const normalizeTrouserLengthMode = (value = 'full') => {
  const normalized = String(value || 'full').toLowerCase();
  return normalized === 'short' || normalized === 'shorts' ? 'short' : 'full';
};

const JACKET_REQUIRED_FIELDS = [
  { key: 'neck', label: 'Neck', min: 1 },
  { key: 'chest', label: 'Chest', min: 1 },
  { key: 'waist', label: 'Waist', min: 1 },
  { key: 'belly', label: 'Belly', min: 1 },
  { key: 'seat', label: 'Seat', min: 1 },
  { key: 'shoulder', label: 'Shoulder', min: 1 },
  { key: 'armholeScyeDepth', label: 'Armscye Depth', min: 1 },
  { key: 'acrossBack', label: 'X-Back', min: 0.5 },
  { key: 'acrossFront', label: 'X-Front', min: 0.5 },
  { key: 'jacketLengthBack', label: 'Length Back', min: 1 },
  { key: 'jacketLengthFront', label: 'Length Front', min: 1 },
  { key: 'jacketSleeveLength', label: 'Sleeve Length', min: 1 },
  { key: 'jacketSleeveBicep', label: 'Bicep (Full)', min: 1 },
  { key: 'jacketSleeveElbow', label: 'Elbow (Full)', min: 1 },
  { key: 'jacketSleeveHem', label: 'Hem (Full)', min: 1 },
];

const JACKET_BELLY_PROFILE_OPTIONS = [
  { value: 'slim', label: 'Slim' },
  { value: 'normal', label: 'Normal' },
  { value: 'slight_belly', label: 'Slight Belly' },
  { value: 'big_belly', label: 'Big Belly' },
  { value: 'extreme_belly', label: 'Extreme Belly' },
];

const JACKET_POSTURE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'stoop', label: 'Stoop' },
  { value: 'sway', label: 'Sway' },
];

const getRequiredFieldsByGarment = (garmentType = '') => {
  const garment = String(garmentType || '').toLowerCase();
  if (garment === 'shirt') return SHIRT_REQUIRED_FIELDS;
  if (garment === 'trouser') return TROUSER_REQUIRED_FIELDS;
  if (garment === 'jacket') return JACKET_REQUIRED_FIELDS;
  return [];
};

export default function DraftMeasurementsModal({
  open,
  garmentType,
  geometryVersion = '',
  baseMeasurements = {},
  onOpenChange,
  onConfirm,
  onCancel,
  confirmLabel = 'Draft Pattern',
}) {
  const requiredFields = useMemo(() => getRequiredFieldsByGarment(garmentType), [garmentType]);
  const [draftValues, setDraftValues] = useState({});
  const [jacketBellyProfile, setJacketBellyProfile] = useState('normal');
  const [jacketPosture, setJacketPosture] = useState('normal');
  const [trouserLengthMode, setTrouserLengthMode] = useState('full');
  const [shirtPortlyType, setShirtPortlyType] = useState('normal');
  const [shirtBodyStyle, setShirtBodyStyle] = useState('gents');
  const isTrouserDraft = String(garmentType || '').toLowerCase() === 'trouser';
  const supportsTrouserShorts = isTrouserDraft && String(geometryVersion || '').toLowerCase() === 'trouser_geom_v4';

  const isShirtDraft = String(garmentType || '').toLowerCase() === 'shirt';
  const visibleRequiredFields = useMemo(() => {
    if (isShirtDraft && shirtBodyStyle === 'ladies') {
      const bustField = { key: 'bust', label: 'Bust (full)', min: 1 };
      const chestIndex = requiredFields.findIndex((field) => field.key === 'chest');
      if (chestIndex >= 0) {
        const ordered = [...requiredFields];
        ordered.splice(chestIndex + 1, 0, bustField);
        return ordered;
      }
      return [...requiredFields, bustField];
    }
    if (!supportsTrouserShorts) return requiredFields;
    if (normalizeTrouserLengthMode(trouserLengthMode) === 'short') {
      return requiredFields.filter((field) => field.key !== 'knee');
    }
    return requiredFields;
  }, [supportsTrouserShorts, requiredFields, trouserLengthMode, isShirtDraft, shirtBodyStyle]);

  useEffect(() => {
    if (!open) return;
    setTrouserLengthMode(supportsTrouserShorts ? normalizeTrouserLengthMode(baseMeasurements?.trouserLengthMode) : 'full');
    const seeded = requiredFields.reduce((acc, field) => {
      const value = Number(baseMeasurements?.[field.key]);
      if (Number.isFinite(value)) {
        acc[field.key] = String(value);
      } else if (Number.isFinite(Number(field.defaultValue))) {
        acc[field.key] = String(field.defaultValue);
      } else {
        acc[field.key] = '';
      }
      return acc;
    }, {});
    setDraftValues(seeded);
  }, [open, requiredFields, baseMeasurements, supportsTrouserShorts]);

  useEffect(() => {
    if (!open) return;
    setShirtPortlyType('normal');
    setShirtBodyStyle('gents');
  }, [open, garmentType]);

  useEffect(() => {
    if (!open) return;
    if (String(garmentType || '').toLowerCase() === 'jacket') {
      setJacketBellyProfile(String(baseMeasurements?.bellyProfile || 'normal').toLowerCase());
      setJacketPosture(String(baseMeasurements?.posture || 'normal').toLowerCase());
      return;
    }
    setJacketBellyProfile('normal');
    setJacketPosture('normal');
  }, [open, garmentType, baseMeasurements]);

  const validation = useMemo(() => {
    return visibleRequiredFields.reduce((acc, field) => {
      const raw = String(draftValues?.[field.key] ?? '').trim();
      const numeric = Number(raw);
      const valid = raw !== '' && Number.isFinite(numeric) && numeric >= field.min;
      acc[field.key] = valid;
      return acc;
    }, {});
  }, [visibleRequiredFields, draftValues]);

  const allValid = visibleRequiredFields.every((field) => validation[field.key]);

  const handleConfirm = () => {
    if (!allValid) return;
    const payload = visibleRequiredFields.reduce((acc, field) => {
      acc[field.key] = Number(draftValues[field.key]);
      return acc;
    }, {});

    if (supportsTrouserShorts) {
      payload.trouserLengthMode = normalizeTrouserLengthMode(trouserLengthMode);
    } else if (String(garmentType || '').toLowerCase() === 'shirt') {
      payload.portlyType = shirtPortlyType;
      payload.shirtBodyStyle = shirtBodyStyle;
      if (shirtBodyStyle === 'ladies') {
        payload.bust = Number(payload.bust || payload.chest + 2);
      }
      payload.frontShoulderSlope = payload.backShoulderSlope;
      if (!Number.isFinite(Number(payload.shirtLengthFront))) {
        const backLen = Number(payload.shirtLengthBack);
        const hpsDrop = Number(payload.backToFtHps);
        if (Number.isFinite(backLen) && Number.isFinite(hpsDrop)) {
          payload.shirtLengthFront = Math.max(1, backLen - hpsDrop);
        }
      }
    } else if (String(garmentType || '').toLowerCase() === 'jacket') {
      payload.bellyProfile = jacketBellyProfile;
      payload.posture = jacketPosture;
    }

    onConfirm?.(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="draft-required-measurements-modal">
        <DialogHeader>
          <DialogTitle data-testid="draft-required-measurements-title">Required Measurements</DialogTitle>
          <DialogDescription data-testid="draft-required-measurements-description">
            Enter required {String(garmentType || '').toLowerCase()} measurements before drafting. Use unit selector in the app for inch/cm workflow.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[58vh] overflow-y-auto pr-1" data-testid="draft-required-measurements-fields-wrap">
          {supportsTrouserShorts && (
            <div className="space-y-1.5" data-testid="draft-required-trouser-length-mode-wrap">
              <Label htmlFor="draft-required-trouser-length-mode-select-trigger">Length</Label>
              <Select value={trouserLengthMode} onValueChange={setTrouserLengthMode}>
                <SelectTrigger id="draft-required-trouser-length-mode-select-trigger" data-testid="draft-required-trouser-length-mode-select-trigger">
                  <SelectValue placeholder="Select trouser length" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full" data-testid="draft-required-trouser-length-mode-option-full">Full</SelectItem>
                  <SelectItem value="short" data-testid="draft-required-trouser-length-mode-option-short">Shorts</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {String(garmentType || '').toLowerCase() === 'shirt' && (
            <div className="space-y-1.5" data-testid="draft-required-shirt-body-style-wrap">
              <Label htmlFor="draft-required-shirt-body-style-select-trigger">Shirt block</Label>
              <Select value={shirtBodyStyle} onValueChange={setShirtBodyStyle}>
                <SelectTrigger id="draft-required-shirt-body-style-select-trigger" data-testid="draft-required-shirt-body-style-select-trigger">
                  <SelectValue placeholder="Gents or Ladies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gents" data-testid="draft-required-shirt-body-style-gents">Gents</SelectItem>
                  <SelectItem value="ladies" data-testid="draft-required-shirt-body-style-ladies">Ladies</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {String(garmentType || '').toLowerCase() === 'shirt' && (
            <div className="space-y-1.5" data-testid="draft-required-shirt-portly-wrap">
              <Label htmlFor="draft-required-shirt-portly-select-trigger">Portly</Label>
              <Select value={shirtPortlyType} onValueChange={setShirtPortlyType}>
                <SelectTrigger id="draft-required-shirt-portly-select-trigger" data-testid="draft-required-shirt-portly-select-trigger">
                  <SelectValue placeholder="Select body type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="thin" data-testid="draft-required-shirt-portly-option-thin">Thin</SelectItem>
                  <SelectItem value="normal" data-testid="draft-required-shirt-portly-option-normal">Normal</SelectItem>
                  <SelectItem value="slight_belly" data-testid="draft-required-shirt-portly-option-slight-belly">Slight Belly</SelectItem>
                  <SelectItem value="big_belly" data-testid="draft-required-shirt-portly-option-big-belly">Big Belly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {String(garmentType || '').toLowerCase() === 'jacket' && (
            <>
              <div className="space-y-1.5" data-testid="draft-required-jacket-belly-profile-wrap">
                <Label htmlFor="draft-required-jacket-belly-profile-select-trigger">Belly</Label>
                <Select value={jacketBellyProfile} onValueChange={setJacketBellyProfile}>
                  <SelectTrigger id="draft-required-jacket-belly-profile-select-trigger" data-testid="draft-required-jacket-belly-profile-select-trigger">
                    <SelectValue placeholder="Select belly profile" />
                  </SelectTrigger>
                  <SelectContent>
                    {JACKET_BELLY_PROFILE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value} data-testid={`draft-required-jacket-belly-profile-option-${option.value}`}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5" data-testid="draft-required-jacket-posture-wrap">
                <Label htmlFor="draft-required-jacket-posture-select-trigger">Posture</Label>
                <Select value={jacketPosture} onValueChange={setJacketPosture}>
                  <SelectTrigger id="draft-required-jacket-posture-select-trigger" data-testid="draft-required-jacket-posture-select-trigger">
                    <SelectValue placeholder="Select posture" />
                  </SelectTrigger>
                  <SelectContent>
                    {JACKET_POSTURE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value} data-testid={`draft-required-jacket-posture-option-${option.value}`}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {visibleRequiredFields.filter((field) => !field.hiddenInput).map((field) => {
            const isValid = Boolean(validation[field.key]);
            return (
              <div className="space-y-1.5" key={field.key}>
                <Label htmlFor={`draft-required-${field.key}`}>{field.label}</Label>
                <Input
                  id={`draft-required-${field.key}`}
                  type="number"
                  step="0.125"
                  min={field.min}
                  value={draftValues[field.key] ?? ''}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setDraftValues((prev) => ({ ...prev, [field.key]: nextValue }));
                  }}
                  className={isValid ? '' : 'border-red-400 bg-red-50/40'}
                  data-testid={`draft-required-input-${field.key}`}
                />
                {!isValid && (
                  <p className="text-xs text-red-600" data-testid={`draft-required-error-${field.key}`}>
                    Enter a valid value (≥ {field.min}).
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onCancel?.();
              onOpenChange?.(false);
            }}
            data-testid="draft-required-measurements-cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!allValid}
            data-testid="draft-required-measurements-confirm-button"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
